import React, { useEffect, useRef, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';

import AskRichardWordmark from '~/components/brand/AskRichardWordmark';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useGetReportingLensDocumentData } from '~/hooks/apis/reporting/useGetReportingLensDocumentData';
import { lensChangeKey } from '~/hooks/apis/reporting/useLensChange';

import type { LensReaderScope } from '../lensReaderScope';
import type { LensEditorPanelState, LensPanelAskRenderArgs } from '../panel/panelTypes';
import { useLensSidebarOpen } from '../panel/useLensSidebarOpen';
import { useLensUndoShortcut } from '../useLensUndoShortcut';

import type { LensFiltered } from '../LensFilterReceipt';
import AddBlock from './AddBlock';
import BlockPane, { type BlockPaneView, type InspectedBlock } from './BlockPane';
import { LINKED_BLOCK_PARAM } from './BlockView';
import BlockSettings from './BlockSettings';
import DataPanel from './DataPanel';
import EditBar, { defaultViewOperations } from './EditBar';
import LensAbout from './LensAbout';
import LensActions from './LensActions';
import LensBoard from './LensBoard';
import LensDownload from './LensDownload';
import LensPanel from './LensPanel';
import LensShare from './LensShare';
import PaneEdge, { PANE_WIDTH, storedPaneWidth, useHostWidth } from './PaneEdge';
import { PanePresentation, type PaneDesignSection } from './PaneSettingsLayout';
import PaneTabs, { type PaneTab, panePanelId, paneTabId } from './PaneTabs';
import { aboutOf } from './Reading';
import { findBlock, pageBlocks } from './editor';
import { humanise } from './format';
import { readLensState, writeLensState } from './lensState';
import { hasTabs, readLensTabs, shownPage, shownTabs, writeLensTabs } from './lensTabs';
import { blockStarters, lensStarters } from './starters';
import { type LensState, asLensDocument, blockKind } from './types';
import useBoardEdit, { LENS_WIDE, boardChangeKey } from './useBoardEdit';

type LensDocumentPageProps = {
  lensId: string;
  renderAskPane?: (args: LensPanelAskRenderArgs) => React.ReactNode;
  askRichardRequest?: number;
  onOpenAskRichardPanel?: () => void;
  onLensContextChange?: (context: LensReaderScope) => void;
  onEditorPanelChange?: (state: LensEditorPanelState) => void;
  onOpenRail?: () => void;
};

const noop = () => undefined;

export default function LensDocumentPage({
  lensId,
  renderAskPane,
  askRichardRequest,
  onOpenAskRichardPanel,
  onLensContextChange,
  onEditorPanelChange,
  onOpenRail,
}: LensDocumentPageProps) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('lg'));
  const [state, setState] = useState<LensState>(() => readLensState());
  const [tabs, setTabs] = useState<Array<string>>(() => readLensTabs());
  const [hostRef, hostWidth] = useHostWidth();
  const [paneWidth, setPaneWidth] = useState(storedPaneWidth);
  const compact = narrow || (hostWidth > 0 && hostWidth - paneWidth < PANE_WIDTH.boardLeast);
  const [askOpen, setAskOpen] = useLensSidebarOpen(compact);
  const detail = useGetReportingLeLens(lensId);
  const lens = detail.data?.lens;
  const document = asLensDocument(lens?.spec);
  const canEdit = Boolean(detail.data?.access.canEdit);
  const shown = document && hasTabs(document.page) ? shownTabs(document.page, tabs) : undefined;
  const { data, isFetching, error, isPlaceholderData, settling, refresh } = useGetReportingLensDocumentData(document ? lensId : undefined, lens?.version, state, shown);
  const unplanned = !isFetching && data && !Object.keys(data.blocks).length && data.problems.length ? [...new Set(data.problems.map((problem) => problem.message))].join(' ') : undefined;
  const failure = (!isFetching && error?.message) || unplanned;
  const askVisible = Boolean(renderAskPane) && askOpen;
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<string>();
  const [paneTab, setPaneTab] = useState<PaneTab>('lens');
  const [paneSection, setPaneSection] = useState<PaneDesignSection>('data');
  const [askedBlock, setAskedBlock] = useState<{ id: string; title: string }>();
  const [inspected, setInspected] = useState<InspectedBlock>();
  const [draft, setDraft] = useState<{ requestId: number; text: string }>();
  const editor = useBoardEdit(lensId, lens?.version);
  const building = editing && !preview;
  const settingsOpen = building;
  const visiblePaneTab = !building && paneTab === 'block' ? 'lens' : paneTab;
  useLensUndoShortcut(lensId, canEdit);
  const saving = useIsMutating({ mutationKey: boardChangeKey(lensId) });
  const changing = useIsMutating({ mutationKey: lensChangeKey(lensId) }) - saving;
  const [selectedId, selectedItem] = selected?.includes('/') ? [selected.slice(0, selected.indexOf('/')), selected.slice(selected.indexOf('/') + 1)] : [selected, undefined];
  const selectedBlock = settingsOpen && document ? findBlock(document.page, selectedId) : undefined;
  const selectedTitle = selectedBlock ? String(selectedBlock.title ?? data?.blocks[selectedBlock.id]?.name ?? selectedBlock.id) : '';
  const paneShown = askOpen;
  const askedBlockSpec = document ? findBlock(document.page, askedBlock?.id) : undefined;

  useEffect(() => writeLensState(state), [state]);
  const settledState = useRef('');
  const stateKey = JSON.stringify(state);
  if (data && !isPlaceholderData) settledState.current = stateKey;
  const readerWaiting = isFetching && isPlaceholderData && stateKey !== settledState.current;
  useEffect(() => {
    setEditing(false);
    setPreview(false);
  }, [lensId]);
  const linkedBlock = useRef(new URLSearchParams(window.location.search).get(LINKED_BLOCK_PARAM));
  useEffect(() => {
    const id = linkedBlock.current;
    const node = id && data ? window.document.querySelector<HTMLElement>(`[data-lens-block="${CSS.escape(id)}"]`) : null;
    if (!node) return undefined;
    linkedBlock.current = null;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.classList.add('linked');
    const timer = window.setTimeout(() => node.classList.remove('linked'), 2600);
    return () => window.clearTimeout(timer);
  }, [data]);
  useEffect(() => {
    const apply = (event: Event) => {
      const receipt = (event as CustomEvent<LensFiltered>).detail;
      if (!receipt || receipt.lensId !== lensId) return;
      setState((current) =>
        receipt.clear
          ? {}
          : { ...current, ...(receipt.state?.period ? { period: receipt.state.period } : {}), filters: { ...current.filters, ...receipt.state?.filters } },
      );
    };
    window.addEventListener('lens-view-filter', apply);
    return () => window.removeEventListener('lens-view-filter', apply);
  }, [lensId]);
  useEffect(() => writeLensTabs(tabs), [tabs]);
  useEffect(() => {
    if (askRichardRequest) {
      setAskOpen(true);
    }
  }, [askRichardRequest, setAskOpen]);
  useEffect(() => {
    onEditorPanelChange?.({ open: paneShown, width: paneShown ? paneWidth : 0 });
  }, [paneShown, paneWidth, onEditorPanelChange]);
  useEffect(() => {
    if (!data || isPlaceholderData) {
      onLensContextChange?.({ lensId, ready: false });
      return;
    }
    const labels = new Map(
      Object.values(data.queries).flatMap((envelope) =>
        envelope.columns.map((column) => [column.name, column.label] as const),
      ),
    );
    onLensContextChange?.({
      lensId,
      ready: true,
      period: {
        from: data.period.current.from,
        to: data.period.current.to,
        summary: `${data.period.label} (${data.period.current.label})`,
      },
      filters: [...Object.entries(state.filters ?? {}), ...Object.entries(state.select ?? {})].map(
        ([column, values]) => ({ label: labels.get(column) ?? column, value: values.join(', ') }),
      ),
    });
  }, [data, isPlaceholderData, lensId, onLensContextChange, state]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;
      if (preview) {
        if (event.key === 'Escape') setPreview(false);
        return;
      }
      if (event.key === 'Escape') {
        if (selected) setSelected(selectedItem ? selectedId : undefined);
        else finishEditing();
      }
      if (selected && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        editor.apply({ op: 'remove', id: selected });
        setSelected(selectedItem ? selectedId : undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, preview, selected, selectedId, selectedItem, editor]);

  useEffect(() => {
    if (!canEdit || editing) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'e' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      if ((event.target as HTMLElement).closest('input, textarea, select, button, a, [contenteditable="true"], [role="dialog"], [role="menu"], [role="tab"]')) return;
      event.preventDefault();
      startEditing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, editing]);

  const picking = !building && Object.keys(state.select ?? {}).length > 0;
  const reading = !building && Boolean(inspected);
  useEffect(() => {
    if (!picking && !reading) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || (event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;
      if (reading) {
        setInspected(undefined);
        setSelected(undefined);
      }
      else setState(({ select: _cleared, ...rest }) => rest);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picking, reading]);

  const startEditing = () => {
    setEditing(true);
    setPreview(false);
    setInspected(undefined);
    setSelected(undefined);
    setPaneTab('lens');
    setAskOpen(true);
  };
  const finishEditing = () => {
    setEditing(false);
    setPreview(false);
    setInspected(undefined);
    setSelected(undefined);
    setPaneTab('lens');
  };
  const editBlock = (blockId: string) => {
    setEditing(true);
    setPreview(false);
    setInspected(undefined);
    setSelected(blockId);
    setPaneTab('block');
    setAskOpen(true);
  };
  const select = (blockId: string | undefined) => {
    setSelected(blockId);
    if (!blockId) setInspected(undefined);
    if (blockId) {
      setPaneTab(building ? 'block' : 'lens');
      setAskOpen(true);
    }
  };
  const [composerFocus, setComposerFocus] = useState(0);
  useEffect(() => {
    if (!composerFocus) return undefined;
    const frame = window.requestAnimationFrame(() => window.document.querySelector<HTMLElement>(`#${panePanelId('ask')} textarea`)?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [composerFocus]);
  const showAsk = () => {
    if (!askVisible) onOpenAskRichardPanel?.();
    setPaneTab('ask');
    setAskOpen(true);
  };
  const askPressed = paneShown && paneTab === 'ask';
  const toggleAsk = () => {
    if (askPressed) return setAskOpen(false);
    showAsk();
    setComposerFocus(Date.now());
  };
  const inspect = (block: { id: string; title: string }, view: BlockPaneView) => {
    if (settingsOpen) select(block.id);
    setInspected({ ...block, view, asked: Date.now() });
    setPaneTab(building ? 'block' : 'lens');
    setAskOpen(true);
  };
  const filterLabel = (column: string) => document?.filters.find((filter) => filter.column === column)?.label ?? humanise(column);
  const aboutBlock = (id: string) => {
    const spec = document ? findBlock(document.page, id) : undefined;
    const planned = data?.blocks[id];
    const query = planned?.query ?? planned?.queries?.[0];
    return spec ? aboutOf(spec, query ? data?.queries[query] : undefined) : undefined;
  };
  const blockPane = (block: InspectedBlock, embedded = false) =>
    lens ? <BlockPane about={aboutBlock(block.id)} block={block} data={data} document={document} embedded={embedded} lensId={lensId} lensVersion={lens.version} onClose={() => setInspected(undefined)} onEdit={canEdit && document ? () => editBlock(block.id) : undefined} onView={(view) => setInspected({ ...block, view, asked: Date.now() })} state={state} /> : null;
  const railButton = onOpenRail ? (
    <IconButton aria-label="Open threads and Lenses" onClick={onOpenRail} size="small">
      <MenuIcon fontSize="small" />
    </IconButton>
  ) : null;
  const onBand = document?.display.tone === 'accent';
  const filled = !onBand && !building;
  const askButton = (narrow: boolean) =>
    renderAskPane ? (
      narrow ? (
        <Tooltip title={askPressed && !editing ? 'Hide askRichard' : 'Ask about this Lens'}>
          <IconButton aria-label={askPressed && !editing ? 'Hide askRichard' : 'Ask about this Lens'} aria-pressed={askPressed} onClick={toggleAsk} size="small">
            <AutoAwesomeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          aria-label={askPressed && !editing ? 'Hide askRichard' : 'Open askRichard'}
          aria-pressed={askPressed}
          onClick={toggleAsk}
          size="small"
          startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
          sx={{ whiteSpace: 'nowrap', ...(onBand ? { color: 'common.white', borderColor: 'rgba(255, 255, 255, 0.6)' } : {}) }}
          variant={filled ? 'contained' : 'outlined'}
        >
          <AskRichardWordmark inverted={onBand || (filled ? theme.palette.mode !== 'dark' : theme.palette.mode === 'dark')} size={14} />
        </Button>
      )
    ) : null;
  const csvBlocks = document
    ? pageBlocks(shownPage(document.page, tabs))
        .map((block) => block.id)
        .filter((id) => data?.blocks[id]?.query || data?.blocks[id]?.queries?.length)
    : [];
  const tools = (narrow: boolean) => ({
    ask: askButton(narrow),
    share: detail.data ? <LensShare detail={detail.data} narrow={narrow} state={state} /> : null,
    download: lens ? <LensDownload blocks={csvBlocks} lensId={lensId} state={state} tabs={shown} title={lens.title} /> : null,
    spill: detail.data ? <LensActions asReader={preview} data={data} detail={detail.data} onEdit={canEdit && !editing ? startEditing : undefined} onRefresh={() => void refresh()} refreshing={isFetching} state={state} /> : null,
    rail: narrow ? railButton : null,
  });

  if (!lens || !document) {
    return (
      <Box aria-busy role={detail.error ? 'alert' : undefined} sx={{ color: 'text.secondary', fontSize: 13, p: 3 }}>
        {detail.error ? detail.error.message : null}
      </Box>
    );
  }

  const defaults = defaultViewOperations(document, state);
  const editBar = editing ? (
    <EditBar
      history={editor}
      lensSettingsOpen={paneShown && paneTab === 'lens'}
      onAddBlock={setAddAnchor}
      onDone={finishEditing}
      onLensSettings={() => {
        setPaneTab('lens');
        setAskOpen(true);
      }}
      onMakeDefault={
        defaults.operations.length
          ? () => {
              const selected = state;
              editor.apply(defaults.operations, (saved) => {
                if (!saved) return;
                setState((current) => {
                  const next = { ...current };
                  if (current.period === selected.period) delete next.period;
                  const filters = { ...current.filters };
                  for (const column of defaults.filters) {
                    if (JSON.stringify(filters[column]) === JSON.stringify(selected.filters?.[column])) delete filters[column];
                  }
                  next.filters = filters;
                  return next;
                });
              });
            }
          : undefined
      }
      onPreview={(on) => {
        setPreview(on);
        setSelected(undefined);
        setInspected(undefined);
      }}
      preview={preview}
    />
  ) : undefined;

  return (
    <Box ref={hostRef} sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        <LensBoard
          data={data}
          document={document}
          editor={building ? editor : undefined}
          failed={failure || undefined}
          retry={() => void refresh()}
          loading={isFetching && isPlaceholderData}
          applying={changing > 0 || editor.pending || settling}
          fading={readerWaiting ? [LENS_WIDE] : changing > 0 || saving > 0 || settling || isFetching ? editor.touched : undefined}
          askAbout={
            renderAskPane
              ? (block, text) => {
                  setAskedBlock(block);
                  if (text) setDraft({ requestId: Date.now(), text });
                  showAsk();
                  setComposerFocus(Date.now());
                }
              : undefined
          }
          askFix={
            renderAskPane
              ? (text) => {
                  setAskedBlock(undefined);
                  setDraft({ requestId: Date.now(), text });
                  showAsk();
                }
              : undefined
          }
          asked={inspected?.id ?? askedBlock?.id}
          follow={(block) => {
            if (settingsOpen) setSelected(block.id);
            setInspected((current) => (current?.id === block.id ? current : { ...block, view: 'about' }));
          }}
          inspect={inspect}
          editBlock={canEdit ? editBlock : undefined}
          certified={detail.data?.certification?.badge.kind === 'certified'}
          editBar={editBar}
          onStateChange={setState}
          onTabsChange={setTabs}
          state={state}
          tabs={tabs}
          theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
          tools={tools}
          selected={selected}
          onSelect={select}
        />
      </Box>
      {building ? <AddBlock anchor={addAnchor} data={data} document={document} editor={editor} onClose={() => setAddAnchor(null)} /> : null}
      {paneShown ? (
        <Box
          sx={{
            borderLeft: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: paneWidth,
            maxWidth: '100%',
            flexShrink: 0,
            bgcolor: 'background.paper',
            position: 'relative',
            ...(compact ? { position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: theme.zIndex.drawer, boxShadow: 8 } : {}),
            '@media print': { display: 'none' },
          }}
        >
          {compact ? null : <PaneEdge most={Math.max(PANE_WIDTH.least, Math.min(PANE_WIDTH.most, hostWidth - PANE_WIDTH.boardLeast))} onCollapse={() => setAskOpen(false)} onWidth={setPaneWidth} width={paneWidth} />}
          <PaneTabs onCollapse={() => setAskOpen(false)} onTab={setPaneTab} tab={visiblePaneTab} withAsk={Boolean(renderAskPane)} withBlock={building} />
          {building ? <Box aria-labelledby={paneTabId('block')} hidden={visiblePaneTab !== 'block'} id={panePanelId('block')} role="tabpanel" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {building || (settingsOpen && selectedBlock) ? (
              <PanePresentation active={building && paneTab === 'block' && !selectedItem} emptyHint={selectedBlock ? 'No setting matches this block.' : 'Select a block to search its settings.'} onSection={setPaneSection} section={paneSection}>
                <BlockSettings
                  block={selectedBlock}
                  data={data}
                  editor={editor}
                  lens={document}
                  family={selectedId ? data?.blocks[selectedId]?.family : undefined}
                  item={selectedItem}
                  view={selectedBlock && typeof selectedBlock.view === 'string' ? document?.views[selectedBlock.view] : undefined}
                  siblings={document ? pageBlocks(document.page) : []}
                  kind={selectedBlock ? blockKind(selectedBlock) ?? data?.blocks[selectedBlock.id]?.kind : undefined}
                  onSelect={select}
                  state={state}
                  howItsMade={selectedBlock && !selectedItem ? blockPane(inspected?.id === selectedBlock.id ? inspected : { id: selectedBlock.id, title: selectedTitle, view: 'about' }, true) : undefined}
                />
              </PanePresentation>
            ) : inspected ? (
              blockPane(inspected)
            ) : (
              <Typography color="text.secondary" sx={{ fontSize: 13, p: 2 }}>
                Select a block on the board.
              </Typography>
            )}
          </Box> : null}
          <Box aria-labelledby={paneTabId('lens')} hidden={visiblePaneTab !== 'lens'} id={panePanelId('lens')} role="tabpanel" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {settingsOpen ? (
              <PanePresentation active={building && paneTab === 'lens'} emptyHint="No Lens setting matches." onSection={setPaneSection} section={paneSection}>
                <LensPanel data={data} dataPanel={<DataPanel data={data} editor={editor} lensId={lensId} lensVersion={lens.version} />} document={document} editor={editor} />
              </PanePresentation>
            ) : inspected ? (
              blockPane(inspected)
            ) : (
              <LensAbout data={data} document={document} filterLabel={filterLabel} state={state} />
            )}
          </Box>
          {renderAskPane ? (
            <Box aria-labelledby={paneTabId('ask')} id={panePanelId('ask')} role="tabpanel" sx={{ display: visiblePaneTab !== 'ask' ? 'none' : 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
              {renderAskPane({
                onApplyProposal: noop,
                onSaveDraft: async () => undefined,
                dirty: false,
                isSaving: false,
                lensTitle: lens.title,
                canEdit,
                onClearCard: () => setAskedBlock(undefined),
                block: askedBlock,
                draft,
                starters: askedBlockSpec ? blockStarters(askedBlockSpec, data, document) : lensStarters(document, data),
              })}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
